
import { Component, Vue, Watch } from "vue-property-decorator";
import { Timetable } from "@/components/Timetable/models/Timetable";
import './TimetableList.scss'
import { Guid } from "guid-typescript";
import Destination from "@/components/Passenger/models/Destination";
import TimetableListItem from "@/components/Timetable/components/TimetableListItem.vue";
import { Passenger } from "@/components/Passenger/models/Passenger";
import Routes from '@/Routes';
import { RolePermission } from '@/models/Role';
import TimetableState, { TimetableStates } from '@/components/Timetable/models/TimetableStates';
import { Ride } from '@/components/Timetable/models/Ride';
import User from '../../../models/User';

@Component({
    components: {
      TimetableListItem
    },
  })
export class TimetableList extends Vue {

    mounting: boolean = true;
    editedTimetableId: Guid = null;
    showTimetableEditor: boolean = false;
    stateFilter: string = 'pending';
    selectableTimetableStates: TimetableState[] = TimetableStates.SelectableStates;

    headers: any[] = [ 
      { 
        i18n: 'timetable.table.header.idNumber',
        text: 'ID', 
        align: 'left', 
        value: 'idNumber',
        hasCustomTemplate: true
      },
      { 
        i18n: 'timetable.table.header.startDate',
        fi: 'Alkaa', 
        text: 'Starts', 
        align: 'left', 
        value: 'startDateString',
        hasCustomTemplate: true
      },
      { 
        i18n: 'timetable.table.header.endDate',
        fi: 'Loppuu', 
        text: 'Ends', 
        align: 'left', 
        value: 'endDateString',
        hasCustomTemplate: true
      },
      { 
        i18n: 'timetable.table.header.note',
        fi: 'Nimi', 
        text: 'Name', 
        align: 'left', 
        value: 'noteString' 
      },
      { 
        i18n: 'timetable.table.header.summary',
        fi: 'Yhteenveto', 
        text: 'Summary', 
        align: 'left', 
        value: 'summary',
        hasCustomTemplate: true
      },
      { 
        i18n: 'timetable.table.header.createdBy',
        fi: 'Tekijä', 
        text: 'Created by', 
        align: 'left', 
        value: 'createdByNameFromEmail'
      },
      { 
        i18n: 'timetable.table.header.actions',
        fi: 'Toiminnot', 
        text: 'Actions', 
        align: 'right', 
        value: 'actions',
        hasCustomTemplate: true
      }
    ]

    get pagination(): any {
      return this.$store.state.app.timetableListDataTablePagination;
    }

    set pagination(val: any){
      this.$store.dispatch("app/setTimetableListDataTablePagination", val);
    }

    get storeRides(): Ride[]{
      return this.$store.state.ride.rides;
    }

    timetableRides(timetable: Timetable): Ride[] {
        return this.filteredDestinationTimetableRides.filter((ride: Ride) => ride.timetableId === timetable.id);
    }

    get filteredDestinationTimetableRides(): Ride[] {      
      const timetableIds = this.filteredDestinationTimetables.map(t => t.id);
      return this.storeRides.filter((ride: Ride) => timetableIds.includes(ride.timetableId));
    }

    timetablePassengersIds(timetable: Timetable): number[] {
      return timetable.timetablePassengers.map(tp => tp.passengerId);
    }

    timetablePassengersWithRidesCount(timetable: Timetable): number {
        return this.timetablePassengersIds(timetable)
          .filter(pid => 
            this.timetableRides(timetable).some((r: Ride) => r.passengerId === pid)
          )
          .length;
    }

    onTableHeaderClick(column){
      const pagination = Object.assign({}, this.pagination);
      if (pagination.sortBy === column) {
        pagination.descending = !pagination.descending;
      } else {
        pagination.sortBy = column;
        pagination.descending = false;
      }
      this.pagination = pagination;
    }

    onDatatableSort(items, index, isDesc) {
      items.sort((a: Timetable, b: Timetable) => {
        if (index === "summary") {
          const passengerCountA = a.passengerWithRidesCount || 0;
          const passengerCountB = b.passengerWithRidesCount || 0;

          if (isDesc) {
            return passengerCountB < passengerCountA ? -1 : 1;
          } 

          return passengerCountA < passengerCountB ? -1 : 1;
        }

        if (isDesc) {
          return b[index] < a[index] ? -1 : 1;
        }

        return a[index] < b[index] ? -1 : 1;
      });

      return items;
    }

    get isLoading(): boolean {
      return this.$store.state.timetable.pending.timetables ||
        this.$store.state.destination.pending.destinations ||
        this.$store.state.ride.pending.rides;
    }

    @Watch("isLoading")
    onIsLoadingChanged(val, oldVal){
      // Dispatch window resize event to make the state tabs autosize
      window.dispatchEvent(new Event("resize"));
    }

    get showExpiredTimetables(): boolean {
        return this.$store.state.app.showExpiredTimetables;
    }
    set showExpiredTimetables(val) {
        this.$store.dispatch("app/setShowExpiredTimetables", val);
    }

    get showOnlyMyTimetables(): boolean {
        return this.$store.state.app.showOnlyMyTimetables;
    }
    set showOnlyMyTimetables(val) {
        this.$store.dispatch("app/setShowOnlyMyTimetables", val);
    }

    get showTimetablesAsList(): boolean {
        return this.$store.state.app.showTimetablesAsList;
    }
    set showTimetablesAsList(val) {
        this.$store.dispatch("app/setShowTimetablesAsList", val);
    }

    get me(): User {
      return this.$store.state.user.me;
    }

    get selectedTimetableId(): Guid {
      return this.$store.state.route.params.timetableId;
    }

    get storeTimetables(): Timetable[] {
      return this.$store.state.timetable.timetables || [];
    }

    get destinationTimetables(): Timetable[] {
      const result = this.storeTimetables.filter(t => 
        t.destinationId === this.routeDestinationId &&
        (!t.hasExpired || this.showExpiredTimetables) &&
        ((t.createdByEmail === this.me.email && this.showOnlyMyTimetables) || !this.showOnlyMyTimetables)
      );

      return result;
    }

    get filteredDestinationTimetables(): Timetable[] {
      return this.destinationTimetables.filter(t => t.state === this.routeStateId);
    }

    get sortedDestinationTimetables(): Timetable[] {
      return this.filteredDestinationTimetables.sort((a, b) => {
        if (a.startDate.getTime() !== b.startDate.getTime() ){
          return a.startDate.getTime() > b.startDate.getTime() ? 1 : -1;
        } 
        if (a.id !== b.id){
          return a.id > b.id ? -1 : 1;
        }
      });
    }

    get destinations(): Destination[] {
      return this.$store.state.destination.destinations;
    }

    get destination(): Destination {
      return this.destinations.find(d => d.id === this.routeDestinationId);
    }

    get routeStateNameLowerCase(): string {
      return this.$store.state.route.params.state;
    }

    get routeState(): TimetableState {
      return this.routeStateNameLowerCase 
        ? TimetableStates.SelectableStates.find(s => s.nameLowerCase === this.routeStateNameLowerCase)
        : null;
    }

    get routeStateId(): number {
      return this.routeState ? this.routeState.id : 0;
    }

    @Watch('routeState', { immediate: true })
    onRouteStateChanged(val, oldVal) {
      if (val){
        this.$store.dispatch("app/setLastSelectedTimetableState", this.routeState);
      }
    }

    timetableCountWithState(stateId: number): number {
      return this.destinationTimetables.filter(t => t.state === stateId).length;
    }

    destinationFor(timetable: Timetable): Destination {
      return this.destinations.find(d => d.id === timetable.destinationId);
    }

    get routeDestinationId(): number {
      return parseInt(this.$store.state.route.params.destinationId, 10);
    }

    get lastSelectedTimetableId(): Guid {
      return this.$store.state.app.lastSelectedTimetableId;
    }

    get userCanCreateTimetables(): boolean {
      return this.me 
        ? this.me.hasPermissions(this.routeDestinationId, [RolePermission.CreateTimetables])
        : false;
    }

    timeout(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }

    get lastSelectedTimetableState(): TimetableState{
      return this.$store.state.app.lastSelectedTimetableState;
    }

    get lastSelectedTimetableStateNameLowerCase(): string {
      return this.lastSelectedTimetableState 
        ? this.lastSelectedTimetableState.nameLowerCase
        : null;
    }

    @Watch('$route', { immediate: true }) onRouteChanged(val, oldVal) {
      if (!this.routeState){
        this.$router.replace({
          name: Routes.timetableListWithStateRoute.name, 
          params: { 
            state: this.lastSelectedTimetableStateNameLowerCase ? this.lastSelectedTimetableStateNameLowerCase : 'new'
          }
        })
      }
    }
    
    async mounted() {
      this.mounting = true;
      if (this.lastSelectedTimetableId && this.showTimetablesAsList){
        // copy the value from store to a local variable, so it's there after the timeout
        const lastSelectedTimetableId = this.lastSelectedTimetableId;
        const timetableElement = document.querySelector("[data-timetable-id='" + lastSelectedTimetableId + "']");

        if (timetableElement){
          await this.timeout(400);       
          
          timetableElement.scrollIntoView({ behavior: "auto", block: "center" });
          this.mounting = false;

          await this.timeout(600);
          timetableElement.classList.add("last-selected")

          await this.timeout(1000);
          timetableElement.classList.remove("last-selected")
        } else {
          this.mounting = false;
        }
        // Now that we are bringing the previously selected timetable into view, we can reset astSelectedTimetableId
        this.$store.dispatch("app/setLastSelectedTimetableId", null);
      } else {
        this.mounting = false;
      }
    }

    get showWeekends() {
      return this.$store.state.user.showWeekends;
    }

    onCreateTimeTableClick() {
      this.$router.push({ name: Routes.timetableListCreateNewRoute.name, params: { state: "new" } });
    }

}

export default TimetableList;
